import { createTheme, ThemeOptions } from "@mui/material";

declare module "@mui/material/styles" {
  interface Palette {
    tertiary: {
      main: string;
      "50": string;
      "100": string;
      "200": string;
      "300": string;
      "400": string;
      "600": string;
      "700": string;
      "800": string;
      "900": string;
      light?: string;
      dark?: string;
      contrastText?: string;
    };
    bg: {
      main: string;
      "50": string;
      "100": string;
      "200": string;
      "300": string;
      "400": string;
      "600": string;
      "700": string;
      "800": string;
      "900": string;
      light?: string;
      dark?: string;
      contrastText?: string;
    };
  }
  interface PaletteOptions {
    tertiary?: {
      main: string;
      "50": string;
      "100": string;
      "200": string;
      "300": string;
      "400": string;
      "600": string;
      "700": string;
      "800": string;
      "900": string;
      light?: string;
      dark?: string;
      contrastText?: string;
    };
    bg?: {
      main: string;
      "50": string;
      "100": string;
      "200": string;
      "300": string;
      "400": string;
      "600": string;
      "700": string;
      "800": string;
      "900": string;
      light?: string;
      dark?: string;
      contrastText?: string;
    };
    gray?: {
      main: string;
      "50": string;
      "100": string;
      "200": string;
      "300": string;
      "400": string;
      "600": string;
      "700": string;
      "800": string;
      "900": string;
      light?: string;
      dark?: string;
      contrastText?: string;
    };
  }

  interface TypographyVariants {
    fontSizeSmall: React.CSSProperties;
    fontSizeMedium: React.CSSProperties;
    fontSizeLarge: React.CSSProperties;
    fontSizeXLarge: React.CSSProperties;
    fontSizeXXLarge: React.CSSProperties;
    openSansSmall: React.CSSProperties;
    openSansMedium: React.CSSProperties;
    openSansLarge: React.CSSProperties;
    openSansXLarge: React.CSSProperties;
    openSansXXLarge: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    fontSizeSmall?: React.CSSProperties;
    fontSizeMedium?: React.CSSProperties;
    fontSizeLarge?: React.CSSProperties;
    fontSizeXLarge?: React.CSSProperties;
    fontSizeXXLarge?: React.CSSProperties;
    openSansSmall?: React.CSSProperties;
    openSansMedium?: React.CSSProperties;
    openSansLarge?: React.CSSProperties;
    openSansXLarge?: React.CSSProperties;
    openSansXXLarge?: React.CSSProperties;
  }

  interface Theme {
    fontSize: {
      xs: string;
      sm: string;
      md: string;
      lg: string;
      xl: string;
      "2xl": string;
      "3xl": string;
      "4xl": string;
      "5xl": string;
      "6xl": string;
      "7xl": string;
      "8xl": string;
      "9xl": string;
    };
  }

  interface ThemeOptions {
    fontSize?: {
      xs: string;
      sm: string;
      md: string;
      lg: string;
      xl: string;
      "2xl": string;
      "3xl": string;
      "4xl": string;
      "5xl": string;
      "6xl": string;
      "7xl": string;
      "8xl": string;
      "9xl": string;
    };
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    fontSizeSmall: true;
    fontSizeMedium: true;
    fontSizeLarge: true;
    fontSizeXLarge: true;
    fontSizeXXLarge: true;
    openSansSmall: true;
    openSansMedium: true;
    openSansLarge: true;
    openSansXLarge: true;
    openSansXXLarge: true;
  }
}

const OPEN_SANS = "'Open Sans', sans-serif";
const LATO = "'Lato', sans-serif";
const FONT_WEIGHT_LIGHT = 300;
const FONT_WEIGHT_REGULAR = 400;
const FONT_WEIGHT_SEMIBOLD = 600;

export const theme: ThemeOptions = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#ebe93e",
      "50": "#fdfdec",
      "100": "#f9f8c3",
      "200": "#f6f5a6",
      "300": "#f2f07e",
      "400": "#efed65",
      "600": "#d6d438",
      "700": "#a7a52c",
      "800": "#818022",
      "900": "#63621a",
    },
    secondary: {
      main: "#7500da",
      "50": "#f1e6fb",
      "100": "#d4b0f4",
      "200": "#c08aee",
      "300": "#a354e6",
      "400": "#9133e1",
      "600": "#6a00c6",
      "700": "#53009b",
      "800": "#400078",
      "900": "#31005c",
    },
    tertiary: {
      main: "#588feb",
      "50": "#eef4fd",
      "100": "#cbdcf9",
      "200": "#b2cbf6",
      "300": "#8fb4f2",
      "400": "#79a5ef",
      "600": "#5082d6",
      "700": "#3e66a7",
      "800": "#304f81",
      "900": "#253c63",
    },
    bg: {
      main: "#152b61",
      "50": "#e8eaef",
      "100": "#b6bdce",
      "200": "#939db6",
      "300": "#627195",
      "400": "#445581",
      "600": "#132758",
      "700": "#0f1f45",
      "800": "#0c1835",
      "900": "#091229",
    },
    success: {
      main: "#18d14c",
      "50": "#e8faed",
      "100": "#b7f1c8",
      "200": "#95eaad",
      "300": "#64e087",
      "400": "#46da70",
      "600": "#16be45",
      "700": "#119436",
      "800": "#0d732a",
      "900": "#0a5820",
    },
    warning: {
      main: "#fb7d1b",
      "50": "#fff2e8",
      "100": "#fed7b8",
      "200": "#fdc396",
      "300": "#fca866",
      "400": "#fc9749",
      "600": "#e47219",
      "700": "#b25913",
      "800": "#8a450f",
      "900": "#69350b",
    },
    error: {
      main: "#fe0b18",
      "50": "#ffe7e8",
      "100": "#ffb3b7",
      "200": "#ff8f95",
      "300": "#fe5c64",
      "400": "#fe3c46",
      "600": "#e70a16",
      "700": "#b40811",
      "800": "#8c060d",
      "900": "#6b050a",
    },
    gray: {
      main: "#535965",
      "50": "#f1f2f4",
      "100": "#d7d9dd",
      "200": "#b9bcc3",
      "300": "#989ca5",
      "400": "#757a86",
      "600": "#3c414c",
      "700": "#2c3039",
      "800": "#1b1f26",
      "900": "#12161b",
    },
  },
  fontSize: {
    xs: "10px",
    sm: "12px",
    md: "14px",
    lg: "16px",
    xl: "18px",
    "2xl": "20px",
    "3xl": "24px",
    "4xl": "30px",
    "5xl": "36px",
    "6xl": "48px",
    "7xl": "60px",
    "8xl": "72px",
    "9xl": "96px",
  },
  typography: {
    h1: {
      fontFamily: OPEN_SANS,
      fontWeight: FONT_WEIGHT_LIGHT,
      fontSize: "96px",
    },
    h2: {
      fontFamily: OPEN_SANS,
      fontWeight: FONT_WEIGHT_LIGHT,
      fontSize: "60px",
    },
    h3: {
      fontFamily: OPEN_SANS,
      fontWeight: FONT_WEIGHT_REGULAR,
      fontSize: "48px",
    },
    h4: {
      fontFamily: OPEN_SANS,
      fontWeight: FONT_WEIGHT_REGULAR,
      fontSize: "34px",
    },
    h5: {
      fontFamily: OPEN_SANS,
      fontWeight: FONT_WEIGHT_REGULAR,
      fontSize: "24px",
    },
    h6: {
      fontFamily: OPEN_SANS,
      fontWeight: FONT_WEIGHT_SEMIBOLD,
      fontSize: "20px",
    },
    subtitle1: {
      fontFamily: OPEN_SANS,
      fontWeight: FONT_WEIGHT_REGULAR,
      fontSize: "16px",
    },
    subtitle2: {
      fontFamily: OPEN_SANS,
      fontWeight: FONT_WEIGHT_SEMIBOLD,
      fontSize: "14px",
    },
    body1: {
      fontFamily: LATO,
      fontWeight: FONT_WEIGHT_REGULAR,
      fontSize: "16px",
    },
    body2: {
      fontFamily: LATO,
      fontWeight: FONT_WEIGHT_REGULAR,
      fontSize: "14px",
    },
    button: {
      fontFamily: LATO,
      fontWeight: FONT_WEIGHT_SEMIBOLD,
      fontSize: "14px",
    },
    caption: {
      fontFamily: LATO,
      fontWeight: FONT_WEIGHT_REGULAR,
      fontSize: "12px",
    },
    overline: {
      fontFamily: LATO,
      fontWeight: FONT_WEIGHT_REGULAR,
      fontSize: "10px",
    },
    fontSizeSmall: {
      fontFamily: LATO,
      fontWeight: FONT_WEIGHT_REGULAR,
      fontSize: "12px",
    },
    fontSizeMedium: {
      fontFamily: LATO,
      fontWeight: FONT_WEIGHT_REGULAR,
      fontSize: "14px",
    },
    fontSizeLarge: {
      fontFamily: LATO,
      fontWeight: FONT_WEIGHT_REGULAR,
      fontSize: "16px",
    },
    fontSizeXLarge: {
      fontFamily: LATO,
      fontWeight: FONT_WEIGHT_REGULAR,
      fontSize: "18px",
    },
    fontSizeXXLarge: {
      fontFamily: LATO,
      fontWeight: FONT_WEIGHT_REGULAR,
      fontSize: "20px",
    },
    openSansSmall: {
      fontFamily: OPEN_SANS,
      fontWeight: FONT_WEIGHT_REGULAR,
      fontSize: "12px",
    },
    openSansMedium: {
      fontFamily: OPEN_SANS,
      fontWeight: FONT_WEIGHT_REGULAR,
      fontSize: "14px",
    },
    openSansLarge: {
      fontFamily: OPEN_SANS,
      fontWeight: FONT_WEIGHT_REGULAR,
      fontSize: "16px",
    },
    openSansXLarge: {
      fontFamily: OPEN_SANS,
      fontWeight: FONT_WEIGHT_REGULAR,
      fontSize: "18px",
    },
    openSansXXLarge: {
      fontFamily: OPEN_SANS,
      fontWeight: FONT_WEIGHT_REGULAR,
      fontSize: "20px",
    },
  },
});
